import { Component, OnInit } from "@angular/core";
import { UserService } from "../services/user.service";
import { OfframpService } from "../services/offramp.service";
import { Router } from "@angular/router";
import { Ng4LoadingSpinnerService } from "ng4-loading-spinner";

import { MatDialog } from "@angular/material";
import * as moment from "moment";
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { NotificationsService } from "angular2-notifications";
import { ColDef, GridOptions, GridApi, ColumnApi } from "ag-grid-community";
import { AddOfframpPayoutGatewayDialog } from "./add-offramp-payout-gateway/add-offramp-payout-gateway.component";

const defaults = {
  notif: {
    timeOut: 3000,
    showProgressBar: true,
    pauseOnHover: false,
    clickToClose: true,
    maxLength: 50,
  },
};

@Component({
  selector: "app-offramp-payout-gateways",
  templateUrl: "./offramp-payout-gateways.component.html",
  styleUrls: ["./offramp-payout-gateways.component.css"],
})
export class OfframpPayoutGatewaysComponent implements OnInit {
  _csrf: string;

  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;
  gridOptions: GridOptions;
  columnDefs: ColDef[] = [];
  rowData: any[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100
  };

  oGateways: any = [];
  gateways: any = [];
  searchValue: string = '';
  // Show only active gateways (filtered in the page, on the loaded records)
  activeOnly: boolean = true;
  filter: any = {
    page: 1,
    limit: 100,
  };
  prev_disabled = true;
  next_disabled = false;

  constructor(
    private router: Router,
    private notifSer: NotificationsService,
    private userService: UserService,
    private offrampService: OfframpService,
    private spinnerService: Ng4LoadingSpinnerService,
    public dialog: MatDialog
  ) {
    this.initializeGrid();
  }

  ngOnInit() {
    this.csrf();
  }

  initializeGrid() {
    this.columnDefs = [
      {
        headerName: 'Action',
        field: 'action',
        cellRenderer: (params) => {
          const element = params.data;
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.alignItems = 'center';

          const editBtn = document.createElement('button');
          editBtn.type = 'button';
          editBtn.className = 'btn btn-sm btn-primary';
          editBtn.title = 'Edit';
          editBtn.style.marginRight = '5px';
          editBtn.innerHTML = '<i class="fa fa-edit"></i>';
          editBtn.onclick = (e) => {
            e.stopPropagation();
            this.editRecord(element);
          };
          container.appendChild(editBtn);

          const deleteBtn = document.createElement('button');
          deleteBtn.type = 'button';
          deleteBtn.className = 'btn btn-sm btn-danger';
          deleteBtn.title = 'Delete';
          deleteBtn.innerHTML = '<i class="fa fa-trash"></i>';
          deleteBtn.onclick = (e) => {
            e.stopPropagation();
            this.deleteRecord(element);
          };
          container.appendChild(deleteBtn);

          return container;
        },
        sortable: false,
        filter: false,
        flex: 1,
        minWidth: 110
      },
      { headerName: 'Name', field: 'name', flex: 2 },
      { headerName: 'Code', field: 'code', flex: 1 },
      { headerName: 'MID', field: 'mid', flex: 1.2 },
      { headerName: 'Short Code', field: 'short_code', flex: 0.9 },
      {
        headerName: 'Maintenance (IST)',
        field: 'maintenance_start',
        valueGetter: (params) => params.data && params.data.maintenance_start ? `${params.data.maintenance_start}-${params.data.maintenance_end}` : '',
        flex: 1.2
      },
      { headerName: 'Priority', field: 'priority', flex: 0.8 },
      {
        headerName: 'Amount Range',
        field: 'min_amount',
        valueGetter: (params) => {
          const data = params.data || {};
          const min = Number(data.min_amount) || 0;
          const max = Number(data.max_amount) || 0;
          return `${min || 'no min'} - ${max || 'no max'}`;
        },
        flex: 1.3,
      },
      {
        headerName: 'Modes',
        field: 'supported_modes',
        // Bank payout modes; none = all
        valueGetter: (params) => {
          const modes = ((params.data && params.data.supported_modes) || []).filter((mode) => mode !== 'UPI');
          return modes.length ? modes.join(', ') : 'All';
        },
        flex: 1
      },
      {
        headerName: 'UPI',
        field: 'upi_supported',
        // Yes with the UPI amount range when one is set, e.g. "Yes (100 - 100000)"
        valueGetter: (params) => {
          if (!params.data || params.data.upi_supported !== true) return 'No';
          const min = Number(params.data.upi_min_amount) || 0;
          const max = Number(params.data.upi_max_amount) || 0;
          return min || max ? `Yes (${min} - ${max || 'no max'})` : 'Yes';
        },
        flex: 1.2,
      },
      {
        headerName: 'Charge',
        field: 'charge_percent',
        valueGetter: (params) => (params.data ? `${Number(params.data.charge_percent) || 0}% / min ${Number(params.data.min_charge) || 0}` : ''),
        flex: 1,
      },
      {
        headerName: 'Low Balance Alert',
        field: 'low_balance_alert',
        valueGetter: (params) => (params.data && Number(params.data.low_balance_alert) > 0 ? params.data.low_balance_alert : 'Off'),
        flex: 1
      },
      {
        headerName: 'Mid Balance Alert',
        field: 'mid_balance_alert',
        valueGetter: (params) => (params.data && Number(params.data.mid_balance_alert) > 0 ? params.data.mid_balance_alert : 'Off'),
        flex: 1
      },
      {
        headerName: 'PAN Above',
        field: 'pan_required_above',
        valueGetter: (params) => (params.data && Number(params.data.pan_required_above) > 0 ? params.data.pan_required_above : 'Not required'),
        flex: 1
      },
      {
        headerName: 'Max Processing',
        field: 'max_processing_orders',
        valueGetter: (params) => {
          const max = params.data ? params.data.max_processing_orders : undefined;
          return max === undefined || max === null || Number(max) < 0 ? 'No limit' : max;
        },
        flex: 1,
      },
      { headerName: 'Min Balance', field: 'min_remaining_balance', flex: 1 },
      {
        headerName: 'WhatsApp Group',
        field: 'whatsapp_group_id',
        valueGetter: (params) => (params.data && params.data.whatsapp_group_id ? params.data.whatsapp_group_id : 'Default'),
        flex: 1.4,
      },
      {
        headerName: 'Paused',
        field: 'is_paused',
        valueGetter: (params) => params.data && params.data.is_paused ? `Yes${params.data.pause_message ? ': ' + params.data.pause_message : ''}` : 'No',
        cellStyle: (params) => params.data && params.data.is_paused ? { background: '#ffe6e6' } : null,
        flex: 1.2
      },
      {
        headerName: 'Status',
        field: 'is_active',
        valueFormatter: (params) => params.value ? 'Active' : 'Inactive',
        flex: 1
      },
      {
        headerName: 'CreatedAt',
        field: 'createdAt',
        valueGetter: (params) => params.data ? (params.data.createdAt || params.data.created_at) : '',
        valueFormatter: (params) => {
          if (params.value) {
            return moment(params.value).format('MMM DD, YYYY hh:mm:ss A');
          }
          return '';
        },
        flex: 1.5
      }
    ];

    this.gridOptions = {
      enableCellTextSelection: true,
      ensureDomOrder: true,
      columnDefs: this.columnDefs,
      defaultColDef: this.defaultColDef,
      rowData: this.rowData,
      pagination: false,
      suppressHorizontalScroll: false,
      enableRangeSelection: true,
      animateRows: true,
      rowHeight: 40,
      headerHeight: 40
    };
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.sizeColumnsToFit();
  }

  csrf() {
    this.userService.csrf().subscribe(
      (success) => {
        this._csrf = success._csrf;
        this.getData();
      },
      (error) => {
        this.notifSer.error("Error", error.error.message, defaults.notif);
      }
    );
  }

  goNext(type = 1) {
    if (type == 1) {
      this.filter.page++;
      this.getData();
    }
  }

  goPrev(type = 1) {
    if (type == 1) {
      if (this.filter.page > 1) {
        this.filter.page--;
        this.getData();
      }
    }
  }

  applyFilter(filterValue) {
    this.searchValue = filterValue || '';
    this.filter.page = 1;
    this.getData();
  }

  clearSearch() {
    this.searchValue = '';
    this.applyFilter('');
  }

  getData() {
    this.spinnerService.show();
    this.filter._csrf = this._csrf;
    this.filter.search = this.searchValue;
    this.offrampService.getAllPayoutGateways(this.filter).subscribe((response: any) => {
      const list = (response && response.data) || [];
      this.gateways = list;
      this.oGateways = list;
      this.applyActiveFilter();

      this.spinnerService.hide();
      this.prev_disabled = this.filter.page <= 1;
      this.next_disabled = !list || list.length < this.filter.limit;
    }, (error) => {
      this.spinnerService.hide();
      this.notifSer.error("Error", (error.error && error.error.message) || "Failed to fetch payout gateways", defaults.notif);
    });
  }

  // Active only on: hide inactive gateways; off: show all loaded records
  applyActiveFilter() {
    this.rowData = this.activeOnly ? (this.oGateways || []).filter((gateway) => gateway.is_active !== false) : (this.oGateways || []);
    if (this.gridApi) {
      this.gridApi.setRowData(this.rowData);
    }
  }

  resetFilters() {
    this.filter = { page: 1, limit: 100 };
    this.searchValue = '';
    this.getData();
  }

  addNewRecord() {
    const newRecord = { name: '', code: '', mid: '', short_code: '', min_remaining_balance: 500, priority: 100, min_amount: 0, max_amount: 0, supported_modes: [], upi_supported: false, upi_min_amount: 0, upi_max_amount: 0, max_processing_orders: -1, pan_required_above: 0, low_balance_alert: 0, mid_balance_alert: 0, charge_percent: 0, min_charge: 0, whatsapp_group_id: '', maintenance_start: '', maintenance_end: '', is_paused: false, pause_message: '', is_active: true };

    const dialogRef = this.dialog.open(AddOfframpPayoutGatewayDialog, {
      width: '640px',
      data: { record: newRecord, _csrf: this._csrf }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.notifSer.success("Success", "Payout gateway added successfully", defaults.notif);
        this.getData();
      }
    });
  }

  editRecord(record: any) {
    const dialogRef = this.dialog.open(AddOfframpPayoutGatewayDialog, {
      width: '640px',
      data: { record: record, _csrf: this._csrf }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.notifSer.success("Success", "Payout gateway updated successfully", defaults.notif);
        this.getData();
      }
    });
  }

  deleteRecord(record: any) {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: `Are you sure you want to delete this payout gateway? It will be marked inactive and can be reactivated from Edit.`,
        buttonText: { ok: 'Yes', cancel: 'No' }
      }
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.spinnerService.show();
        this.offrampService.deletePayoutGateway({ _id: record._id, _csrf: this._csrf }).subscribe(
          (response: any) => {
            this.spinnerService.hide();
            if (response.status || response.success) {
              this.notifSer.success("Success", response.message || "Payout gateway deleted successfully", defaults.notif);
              this.getData();
            } else {
              this.notifSer.error("Error", response.message || "Failed to delete payout gateway", defaults.notif);
            }
          },
          (error) => {
            this.spinnerService.hide();
            this.notifSer.error("Error", (error.error && error.error.message) || "Failed to delete payout gateway", defaults.notif);
          }
        );
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/offramp-payout-gateways/offramp-payout-gateways.component.ts